import React, { useCallback, useEffect, useState } from "react";
import {
  SALES_RETURN_ADD,
  SALES_RETURN_ADD_BY_TAX_INVOICE,
  SALES_RETURN_EDIT,
  SALES_RETURN_TITLE,
  SALES_RETURN_VIEW,
} from "../../../Routes/SalesAndPurchase/SalesReturn";
import { Badge, Breadcrumb, Col, Row } from "react-bootstrap";
import CustomButton from "../../../Components/Button/CustomButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomFilter, { InputValues } from "../../../Components/CustomFilter";
import { FilterOperator } from "@tailflow/laravel-orion/lib/drivers/default/enums/filterOperator";
import { SalesReturns } from "../../../Model/SalesAndPurchase/SalesReturn/SalesReturn";
import { setToast } from "../../../Util/Toast";
import { fetchData } from "../../../Util/OrionList";
import CustomDataTable from "../../../Components/CustomDataTable";
import { TableColumn } from "react-data-table-component";
import CustomEditButton from "../../../Components/Button/CustomEditButton";
import CustomViewButton from "../../../Components/Button/CustomViewButton";
import CustomDownloadButton from "../../../Components/Button/CustomDownloadButton";
import { getCookie } from "typescript-cookie";
import { CapitalizeAndRemoveUnderscore } from "../../../Util/CapitalizeAndRemoveUnderscore";
import pluralize from "pluralize";

interface SalesReturnItem {
  id: number;
  title: string;
  description: string;
  status: string;
  days: number;
  delivery_date: string;
  created_at: string;
  document_number: string;
  transporter_name: string;
  payment_date: string;
  counter_party_company: any;
  manufacture: any;
  approve_status: string;
  approve_date: string;
  approved_by: any;
}
const List = ({ permissions }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { tax_invoice_id, date } = useParams();
  // CustomFilter State
  const [inputValues, setInputValues] = useState<InputValues>(
    tax_invoice_id ? { tax_invoice_id: { value: tax_invoice_id } } : {},
  );
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Data State
  const [data, setData] = useState<SalesReturnItem[]>([]);

  // General State
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(
    Number(searchParams.get("perPage")) === 0
      ? 10
      : Number(searchParams.get("perPage")),
  );
  const [page, setPage] = useState(
    Number(searchParams.get("page")) === 0
      ? 1
      : Number(searchParams.get("page")),
  );
  const [totalRows, setTotalRows] = useState(0);
  const [sortField] = useState<string>("id");
  const [sortOrder] = useState<string>("desc");
  const params = date ? new URLSearchParams(date) : null;
  const fromDate = params?.get("fromDate");
  const [isArchive] = useState<boolean>(false);
  const [includeWith] = useState([
    "counterPartyCompany",
    "manufacture",
    "approvedBy",
  ]);
  const isForDashboard = new URLSearchParams(useLocation().search).has(
    "isfordashboard",
  );
  const handleRowCreateButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (tax_invoice_id) {
      navigate(
        SALES_RETURN_ADD_BY_TAX_INVOICE.replace(
          ":tax_invoice_id",
          tax_invoice_id,
        ),
      );
    } else {
      navigate(SALES_RETURN_ADD);
    }
  };

  const filterOptions: any = [
    {
      id: "id",
      value: "id",
      label: "Id",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "document_number",
      value: "document_number",
      label: "Document Number",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "counterPartyCompany.company_name",
      value: "counterPartyCompany.company_name",
      label: "Supplier Name",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "manufacture.company_name",
      value: "manufacture.company_name",
      label: "Manufacturer",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "approve_status",
      value: "approve_status",
      label: "Approve Status",
      fieldType: "select",
      fieldOption: {
        All: "",
        Approved: "approved",
        Rejected: "rejected",
        "Approval Waiting": "approval_waiting",
      },
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "approvedBy.first_name",
      value: "approvedBy.first_name",
      label: "Approved/Rejected By First Name",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "approvedBy.last_name",
      value: "approvedBy.last_name",
      label: "Approved/Rejected By Last Name",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "approve_date",
      value: "approve_date",
      label: "Approved / Rejected Date",
      fieldType: "date",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "status",
      value: "status",
      label: "Status",
      fieldType: "select",
      fieldOption: {
        All: "",
        Draft: "draft",
        Sent: "sent",
      },
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "delivery_date",
      value: "delivery_date",
      label: "Delivery Date",
      fieldType: "date",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "created_at",
      value: "created_at",
      label: "Created Date",
      fieldType: "date",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "updated_at",
      value: "updated_at",
      label: "Updated Date",
      fieldType: "date",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
  ];

  const handlePDFDownloadButtonClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    try {
      const authToken = getCookie("authToken");
      const token = authToken ? authToken.replace(/["']/g, "") : "";
      const apiUrl = process.env.REACT_APP_API_URL + `api/sales-returns/pdf/`;
      const response = await fetch(apiUrl + id, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        const anchor = document.createElement("a");
        anchor.href = responseData.data;
        anchor.download = "purchase order";
        anchor.rel = "noreferrer";
        anchor.target = "_blank";
        anchor.click();
      } else {
        setToast("error", "An error occurred while processing your request");
      }
    } catch (error) {
      setToast("error", "An error occurred while processing your request");
    }
  };

  const handleRowViewButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.preventDefault();
    navigate(SALES_RETURN_VIEW.replace(":id", id.toString()));
  };

  const handleRowEditButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.preventDefault();
    navigate(SALES_RETURN_EDIT.replace(":id", id.toString()));
  };

  const columns: TableColumn<SalesReturnItem>[] = [
    {
      name: "Id",
      maxWidth: "1%",
      cell: (row) => row.id,
    },
    {
      name: "Document Number",
      cell: (row) => row.document_number,
    },
    {
      name: "Suppliers Name",
      cell: (row) => row.counter_party_company?.company_name,
    },
    {
      name: "Manufacturer",
      cell: (row) => row.manufacture?.company_name,
    },
    {
      name: "Status",
      minWidth: "5%",
      cell: (row) => (
        <Badge pill bg={row.status === "draft" ? "secondary" : "primary"}>
          {CapitalizeAndRemoveUnderscore(row.status)}
        </Badge>
      ),
    },
    {
      name: "Approval Status",
      cell: (row) => {
        let badgeVariant = "danger"; // default
        if (row.approve_status === "approved") {
          badgeVariant = "success";
        } else if (row.approve_status === "approval_waiting") {
          badgeVariant = "primary";
        }

        return (
          <Badge pill bg={badgeVariant}>
            {CapitalizeAndRemoveUnderscore(row.approve_status)}
          </Badge>
        );
      },
    },
    {
      name: "Approved/Rejected By",
      minWidth: "12%",
      style: { justifyContent: "center" },
      cell: (row) =>
        `${row.approved_by?.first_name || ""} ${
          row.approved_by?.last_name || ""
        }`.trim(),
    },
    {
      name: "Approval Date",
      cell: (row: any) => row.approve_date,
    },
    {
      name: "Delivery Date",
      cell: (row: any) => row.delivery_date,
    },
    {
      name: "Created At",
      cell: (row: any) => row.created_at,
    },
    {
      name: "Updated At",
      cell: (row: any) => row.updated_at,
    },
    {
      name: "Download",
      button: true,
      cell: (row: any) => (
        <div>
          {row.approve_status === "approved" && (
            <CustomDownloadButton
              id={row.id}
              onClick={handlePDFDownloadButtonClick}
            />
          )}
        </div>
      ),
    },
    {
      name: "Actions",
      button: true,
      wrap: true,
      cell: (row: any) => (
        <div className="d-flex align-items-center">
          {permissions.view && (
            <CustomViewButton
              id={row.id}
              onClick={(e) => {
                return handleRowViewButtonClick(e, row.id);
              }}
            />
          )}
          {permissions.update && (
            <CustomEditButton
              isDisabled={row.status === "sent" && true}
              id={row.id}
              onClick={handleRowEditButtonClick}
            />
          )}
        </div>
      ),
    },
  ];
  const handlePerRowsChange = async (rowLimit: any, page: any) => {
    if (limit === rowLimit) {
      setLimit(rowLimit);
      setPage(page);
    } else {
      setLimit(rowLimit);
      setPage(1);
    }
  };
  const handlePageChange = (page: any) => {
    setPage(page);
  };

  //   api modal function
  const fetchSalesReturn = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        ...(fromDate
          ? { created_at: { filter_operation: ">=", value: fromDate } }
          : {}),
        ...(inputValues ? inputValues : {}),
      };

      await fetchData(
        page,
        limit,
        filters,
        setLoading,
        setData,
        setTotalRows,
        navigate,
        new SalesReturns(),
        sortField,
        sortOrder,
        isArchive,
        includeWith,
      );
    } catch (error: any) {
      setToast("error", error.response.data.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, page, limit, inputValues, sortField, sortOrder, navigate]);
  useEffect(() => {
    fetchSalesReturn();
  }, [fetchSalesReturn]);

  useEffect(() => {
    fetchSalesReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page]);
  const handleGotoDashboard = () => {
    navigate("/");
  };
  return (
    <>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active className="fw-bold">
          Sales Return
        </Breadcrumb.Item>
      </Breadcrumb>
      <Row className="align-items-center">
        <Col className="col-10">
          <div className="d-flex justify-content-start">
            <div style={{ display: "inline-block" }}>
              <h6 className="mt-1">{pluralize(SALES_RETURN_TITLE)} </h6>
            </div>
            <CustomFilter
              options={filterOptions}
              inputValues={inputValues}
              setInputValues={setInputValues}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              getData={fetchSalesReturn}
              setPage={setPage}
            />
          </div>
        </Col>
        <Col className="col-2 text-xxl-end text-xl-end text-lg-end text-md-start text-sm-start text-xs-start">
          {permissions.add && (
            <CustomButton
              label={`Add`}
              variant="success"
              onClick={handleRowCreateButtonClick}
            />
          )}
          {isForDashboard && (
            <button
              type="button"
              className="btn btn-secondary btn-sm ms-2"
              onClick={handleGotoDashboard}
            >
              Back
            </button>
          )}
        </Col>
      </Row>
      <div className="mb-4">
        <CustomDataTable
          columns={columns}
          data={data}
          progressPending={loading}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          handleRowCreateButtonClick={handleRowCreateButtonClick}
          currentPages={page}
          currentRowsPerPage={limit}
          module={"SalesReturns"} // Pass optional fields to CustomDataTable
        />
      </div>
    </>
  );
};

export default List;
